<template>
  <!-- Home ("/" landing; the demo home feeds it static data via `demoData`). Read top
       to bottom as the four questions a manager asks each morning:
         1. How am I doing?        → Allen's briefing (+ "Delivered by Allen" when measured)
         2. Does anything need me? → "Plan ahead" (weather) + "Needs your attention"
                                     (warnings only, max 3)
         3. What should I do?      → Where to focus: top 3 by € within reach; reach
                                     (visitor) cards in their own section, same design
         4. Is it working?         → Store health scorecard, Trends & week ahead, then
                                     the nudge / goals that have no focus card
       One page for every store type — it reads the store's own zones: no polygon product
       zone → footfall headline + ATTRACT in visitors; no checkout zones → those rows are
       named in one "Allen can also show…" line instead. Each figure is stated ONCE.
       Redesign approved on the demo for mixed / polygon-only / tripwire-only stores
       (2026-10-13 → 10-16; review those on /storalytic_demo_store?scenario=…). -->
  <div v-if="!loading && hasAnyZones" class="today">
    <!-- 1 + 2 — Allen's briefing, then only what needs the manager -->
    <section class="today-sec">
      <div class="allen-briefing">
        <div class="allen-briefing-head">
          <span class="allen-briefing-avatar" aria-hidden="true"><i class="bi bi-robot"></i></span>
          <div class="allen-briefing-id">
            <span class="allen-briefing-title">{{ t('today.briefing.title') }}</span>
            <span class="allen-briefing-meta">
              {{ t('today.briefing.meta', { company: companyName }) }}
              <span class="briefing-basis">{{ t('today.briefing.basis') }}</span>
            </span>
          </div>
          <!-- The ONE place Home states delivered value — the same figure + wording as
               the Actions tab's top-right "Delivered by Allen" (cumulative, measured). -->
          <router-link v-if="valueDelivered" :to="actionsHref" class="proof-badge"
                       :title="t('today.briefing.proofTitle', { eur: eur(valueDelivered.runRate) })">
            <i class="bi bi-stars"></i>
            <span class="proof-lbl">{{ t('today.briefing.delivered') }}</span>
            <span class="proof-eur">+{{ eur(valueDelivered.cumulative) }}</span>
            <span class="proof-lbl">{{ t('today.briefing.since', { since: deliveredSince }) }}</span>
          </router-link>
        </div>
        <!-- Reach-only store (every product zone a counting line): no €, engagement or
             conversion exists, so the headline is what it CAN say — its footfall. -->
        <div v-if="reachOnly" class="footfall-head">
          <h2 class="footfall-title">
            <span class="footfall-n">{{ int(storeVisitors) }}</span> {{ t('today.footfall.visitors') }}
            <span v-if="visitorsPerDay" class="footfall-sub">{{ t('today.footfall.perDay', { n: int(visitorsPerDay) }) }}<template v-if="storeVisitLabel">{{ t('today.footfall.stay', { dur: storeVisitLabel }) }}<template v-if="!isNl">{{ ' ' }}<em class="footfall-est">{{ t('today.est') }}</em></template></template>.</span>
          </h2>
        </div>
        <TodayHero
          v-else
          :narrative-only="true"
          :fixed-widgets-data="fixedWidgetsData"
          :selected-duration="selectedDuration"
          :segment-key="companySector"
          :priorities="priorities"
          :store-within-reach-eur="storeWithinReach"
          :has-any-zones="hasAnyZones"
          :strategic-by-zone="strategicByZone"
          :zone-overrides="zoneOverrides"
          :show-within-reach="false"
        />
        <!-- Zone identity 6.4: the briefing was written last night, before the change. -->
        <ChangeNotice v-if="anyTypeChange(changesRegister)" :compact="true" :text="NOTICES.storeValues" />
        <CoachDigest class="allen-briefing-read" :bare="true" :static-digest="digestForView" />
        <AllenNoticed
          v-if="planAheadFindings.length"
          class="allen-briefing-flagged"
          :bare="true"
          :label="t('today.briefing.planAhead')"
          :findings="planAheadFindings"
        />
        <AllenNoticed
          v-if="attentionShown.length"
          class="allen-briefing-flagged"
          :class="{ 'flagged-follow': planAheadFindings.length }"
          :bare="true"
          :label="t('today.briefing.attention')"
          :findings="attentionShown"
        />
        <button
          v-if="notifCenter.allen.length"
          type="button"
          class="flagged-seeall"
          @click="notifCenter.openTo('allen')"
        >
          {{ t('today.briefing.seeAll', { n: notifCenter.allen.length }) }} <i class="bi bi-arrow-right"></i>
        </button>
      </div>
    </section>

    <!-- 3 — Where to focus (top 3; a goal that names the zone shows on its card) -->
    <section class="today-sec">
      <header class="today-head">
        <h3 class="today-head-title">
          <i class="bi bi-bullseye"></i> {{ t('today.focus.title') }}
          <span class="today-basis">· {{ focusBasis }}</span>
        </h3>
        <router-link :to="actionsHref" class="today-link">{{ t('today.focus.seeAll') }} <i class="bi bi-arrow-right"></i></router-link>
      </header>
      <!-- Zone identity 6.7: the € on these cards follow a money-settings change tonight. -->
      <ChangeNotice v-if="settingChanged(changesRegister, 'money')" :compact="true" :text="NOTICES.settings.money" />
      <PrioritizationOpportunities
        :limit="3"
        :lean="true"
        act-tab="actions-layer"
        :fixed-widgets-data="fixedWidgetsData"
        :fixed-widgets-compare-to-data="fixedWidgetsCompareToData"
        :selected-duration="selectedDuration"
        :is-active="true"
        :strategic-by-zone="strategicByZone"
        :segment-key="companySector"
        :zone-overrides="zoneOverrides"
        :recommendations="guidanceRecommendations"
        :goals="goals"
        :new-zones="newSuggestionZones"
        :reach-basis-line="false"
        @shown-zones="(z) => (focusZones = z)"
        @navigate="goToDashboard"
      />
    </section>

    <!-- Store health — a three-row scorecard -->
    <section class="today-sec">
      <header class="today-head">
        <h3 class="today-head-title"><i class="bi bi-speedometer2"></i> {{ t('today.health.title') }}</h3>
        <Tooltip
          class="today-head-tip"
          :text="t('today.health.tip')"
          placement="left"
        >
          <i class="bi bi-info-circle"></i>
        </Tooltip>
      </header>
      <!-- Zone identity 6.4: the gauges were computed last night, before the change. -->
      <ChangeNotice v-if="anyTypeChange(changesRegister)" :compact="true" :text="NOTICES.storeValues" />
      <!-- 6.7: ATTRACT's € within reach and DEPLOY's idle € follow tonight. -->
      <ChangeNotice v-if="settingChanged(changesRegister, 'money')" :compact="true" :text="NOTICES.settings.money" />
      <!-- One scorecard, three rows. ATTRACT and SERVE share one % scale (axisMax), so
           "your best" lines up down the block; DEPLOY keeps its own in-use/idle scale. -->
      <div class="health-rows">
        <!-- Reach-only store: ATTRACT in VISITORS — how many shoppers each counting zone
             draws against its own best weeks (the same numbers as the reach cards above). -->
        <div v-if="reachOnly" class="health-row-tap" role="button" tabindex="0"
             @click="onGaugeTap('attract')" @keydown.enter="onGaugeTap('attract')">
          <HealthRow
            :eyebrow="t('today.health.attract.eyebrow')"
            :subtitle="t('today.health.attract.subtitle')"
            accent="#4F7EB3"
            :help="false"
            :value="`+${int(reachWithin)}`"
            :value-unit="t('today.health.attract.unit')"
            :value-sub="t('today.health.attract.valueSub')"
            :meta="reachMeta"
            :pct="reachStorePct"
            :pct-sub="t('today.health.attract.pctSub')"
            bar="ladder"
            :rungs="[{ key: 'best', name: t('today.health.attract.yourBest'), pct: 100 }]"
            :axis-max="sharedAxisMax || 110"
            :sub-rows="reachRows"
          />
        </div>
        <div v-else class="health-row-tap" role="button" tabindex="0"
             @click="onGaugeTap('attract')" @keydown.enter="onGaugeTap('attract')">
          <StoreEfficiencyGauge
            :axis-max="sharedAxisMax"
            @axis-top="(v) => (axisTops.attract = v)"
            :fixed-widgets-data="fixedWidgetsData"
            :fixed-widgets-compare-to-data="fixedWidgetsCompareToData"
            :segment-key="companySector"
            :zone-overrides="zoneOverrides"
            :compare-to-label="t('today.health.priorPeriod')"
            :has-product-zones="hasProductZones"
            :has-measurable-product-zones="productZoneCount > 0"
            :product-zone-count="productZoneCount"
            :within-reach-eur="storeWithinReach"
            accent="#4F7EB3"
          />
        </div>
        <div v-if="hasWaitingZones" class="health-row-tap" role="button" tabindex="0"
             @click="onGaugeTap('serve')" @keydown.enter="onGaugeTap('serve')">
          <StoreServiceGauge
            :axis-max="sharedAxisMax"
            @axis-top="(v) => (axisTops.serve = v)"
            :fixed-widgets-data="fixedWidgetsData"
            :fixed-widgets-compare-to-data="fixedWidgetsCompareToData"
            :prev-period-label="t('today.health.priorPeriod')"
            :segment-key="companySector"
            :has-waiting-zones="hasWaitingZones"
            :waiting-lane-count="waitingZoneCount"
            :service-zone-count="serviceZoneCount"
            :info-zone-count="infoZoneCount"
            :pick-up-zone-count="pickUpZoneCount"
            accent="#97a83f"
          />
        </div>
        <div v-if="hasInteractionZones" class="health-row-tap" role="button" tabindex="0"
             @click="onGaugeTap('deploy')" @keydown.enter="onGaugeTap('deploy')">
          <StoreCapacityGauge
            :fixed-widgets-data="fixedWidgetsData"
            :fixed-widgets-compare-to-data="fixedWidgetsCompareToData"
            :segment-key="companySector"
            :company-staff-cost="companyStaffCost"
            :prev-period-label="t('today.health.priorPeriod')"
            :has-checkout-zones="hasInteractionZones"
            :lane-count-interaction="interactionLaneCount"
            :lane-count-cashier="cashierLaneCount"
            :idle-when="idleWhen"
            accent="#2f5d8f"
          />
        </div>
      </div>
      <!-- What more zones would add — ONE slim line instead of a "Not configured" row per
           gauge. Admins only (they can arrange it); dismissible for 30 days. -->
      <div v-if="unlockItems.length && showUnlock" class="unlock-strip">
        <i class="bi bi-plus-circle unlock-icon"></i>
        <span class="unlock-body">
          <strong>{{ t('today.health.unlockLead') }}</strong> {{ unlockText }}.
        </span>
        <button type="button" class="unlock-close" :aria-label="t('today.health.hide30')" @click="dismissUnlock">
          <i class="bi bi-x"></i>
        </button>
      </div>
    </section>

    <!-- Context — trends, forecasts and weather: worth knowing, nothing to act on today -->
    <section v-if="contextFindings.length" class="today-sec">
      <header class="today-head">
        <h3 class="today-head-title"><i class="bi bi-calendar-week"></i> {{ t('today.trends.title') }}</h3>
      </header>
      <ul class="ahead-grid">
        <li v-for="f in contextFindings" :key="f.id" class="ahead-item" :class="f.accent ? `accent-${f.accent}` : ''">
          <i class="bi ahead-glyph" :class="f.icon || aheadIcon(f)" aria-hidden="true"></i>
          <div class="ahead-body">
            <span class="ahead-title">{{ f.title }}</span>
            <span class="ahead-detail">{{ f.detail }}</span>
            <span v-if="f.when" class="ahead-when">{{ f.when }}</span>
          </div>
        </li>
      </ul>
    </section>

    <!-- 4 — Is it working? Delivered value is the header badge (stated once); here only
         the nudge when nothing is measured yet, and goals with no focus card of their own. -->
    <section v-if="!valueDelivered || goalsWithoutCard.length" class="today-sec today-close">
      <div v-if="!valueDelivered" class="value-strip value-strip--nudge">
        <i class="bi bi-lightbulb value-strip-icon"></i>
        <span class="value-strip-body">
          <strong>{{ t('today.close.nothingMeasured') }}</strong>
          <span v-if="reachOnly">{{ t('today.close.nudgeReach') }}</span>
          <span v-else>{{ t('today.close.nudgeEur') }}</span>
        </span>
      </div>

      <div v-if="goalsWithoutCard.length" class="goals-rest">
        <span class="goals-rest-lbl"><i class="bi bi-flag"></i> {{ t('today.close.otherGoals') }}</span>
        <span v-for="g in goalsWithoutCard" :key="g.title" class="goals-rest-item">
          <span class="goal-dot" :class="g.lever ? `lever-${g.lever}` : ''"></span>{{ g.title }}
          <span v-if="typeChangedZone(g.zoneKey, g.zone)" class="goal-type-note">
            <i class="bi bi-arrow-repeat"></i> {{ NOTICES.goal }}
          </span>
        </span>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatNumber, formatEuro } from '@/i18n/format'
import { useNotificationCenterStore } from '@/stores/NotificationCenterStore'
import TodayHero from '@/components/ui/TodayHero.vue'
import CoachDigest from '@/components/CoachDigest.vue'
import AllenNoticed from '@/components/ui/AllenNoticed.vue'
import StoreEfficiencyGauge from '@/components/StoreEfficiencyGauge.vue'
import HealthRow from '@/components/ui/HealthRow.vue'
import { useUserStore } from '@/stores/UserStore'
import StoreServiceGauge from '@/components/StoreServiceGauge.vue'
import StoreCapacityGauge from '@/components/StoreCapacityGauge.vue'
import PrioritizationOpportunities from '@/components/PrioritizationOpportunities.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import { getFixedDashboardCountersValues, getZoneTypeAnalytics, getCheckoutIdlePattern } from '@/api/eventsAPI.js'
import { computePriorities, buildZoneOverridesMap, sumWithinReach } from '@/prioritization/priorities'
import { composeStrategicPositioning } from '@/prioritization/strategicPositioning'
import { useCompanyStore } from '@/stores/CompanyStore'
import { useDetectionZoneStore } from '@/stores/DetectionZone'
import { useChangesTodayStore } from '@/stores/ChangesToday'
import { NOTICES, aboutTypeChangedZone, anyTypeChange, settingChanged } from '@/zones/changesToday'
import ChangeNotice from '@/components/ui/ChangeNotice.vue'

const props = defineProps({
  // Demo escape hatch: when set, TodayView renders from this STATIC bundle and
  // makes NO API calls — so the unauthenticated demo home can show the exact same
  // production component. Shape: { companyName, fixedWidgetsData, compareData,
  // sector, zoneOverrides, recommendations, findings:[{id,severity,title,detail,when}], digest }.
  // Omit it entirely on production, where the component self-fetches live data.
  demoData: { type: Object, default: null },
})
// Emitted once, when ALL of Today's data has loaded: 'resolved' carries whether
// Today has content (a measured store w/ product zones). The host waits for this
// before revealing anything — so the sections never pop in one by one — and uses
// the boolean to pick Today vs. its legacy fallback.
const emit = defineEmits(['resolved'])
const isDemo = computed(() => !!props.demoData)
// English keeps its exact formats ('en-US' numbers, €1,234); Dutch numbers and €
// go through i18n/format.js. Text from the backend (findings, digest) is already
// in the reader's language and is shown as it comes.
const { t, locale } = useI18n()
const isNl = computed(() => locale.value === 'nl')
const int = (n) => (isNl.value ? formatNumber(n, { lang: 'nl' }) : Number(n).toLocaleString('en-US'))
const eur = (n) => (isNl.value ? formatEuro(n, { lang: 'nl' }) : `€${Number(n).toLocaleString()}`)
// One loading gate for the whole surface: render nothing until every fetch is in,
// so the host can show a single loader instead of the hero flashing 0%→real, the
// digest arriving late, and the flagged list appearing first.
const loading = ref(!props.demoData)

const companyStore = useCompanyStore()
// Zone identity 6.3: what changed type today (demo: the scenario's register).
const changesTodayStore = useChangesTodayStore()
const changesRegister = computed(() => (isDemo.value
  ? (props.demoData.changesToday || { zones: {}, settings: {} })
  : changesTodayStore.register))
const typeChangedZone = (key, name) => aboutTypeChangedZone(changesRegister.value, { key, name })
const detectionZoneStore = useDetectionZoneStore()
const router = useRouter()
const notifCenter = useNotificationCenterStore()

const selectedDuration = 30
const openingHoursMode = 'only_regular_opening_hours'

// Where the "See all" / "Proof" / "Act on this" links point — the deep dashboard.
const dashHref = computed(() => isDemo.value ? '/storalytic_demo_store/store_level' : '/store_level')
// Navigate to the dashboard, optionally opening a specific tab (?tab=…). The lean
// cards emit their actTab ('actions-layer'), so "Act on this" lands on Actions.
const goToDashboard = (tab, zone) => {
  const query = {}
  if (typeof tab === 'string' && tab) query.tab = tab
  if (zone) query.zone = zone   // deep-link: land on this zone's card in Actions
  router.push(Object.keys(query).length ? { path: dashHref.value, query } : { path: dashHref.value })
}
const actionsHref = computed(() => ({ path: dashHref.value, query: { tab: 'actions-layer' } }))
// Gauge tap-in: ATTRACT drills into the zones (product-zone detail); SERVE/DEPLOY
// into the checkout data. The dashboard reads ?tab= on mount to open the right tab.
function onGaugeTap(key) {
  // ATTRACT (commercial) → the Zones tab (its funnel/roles intelligence).
  // SERVE / DEPLOY (checkout) have no intelligence surface, so they open their
  // raw-data Explore sub-tab directly.
  if (key === 'attract') {
    router.push({ path: dashHref.value, query: { tab: 'intelligence-layer' } })
    return
  }
  const sub = key === 'serve' ? 'sec-checkout-waiting' : 'sec-checkout-interaction'
  router.push({ path: dashHref.value, query: { tab: 'data-foundation-layer', sub } })
}

// Live-fetched holders (production). In demo mode the sources below read straight
// from props.demoData so async demo data that arrives AFTER mount (zones, compare)
// stays reactive — copying into a ref once would freeze it to its initial empty value.
const liveFixedWidgets = ref({})
const liveCompare = ref({})
const liveZones = ref([])
const liveRecs = ref({})
const liveDigest = ref(null)

const fixedWidgetsData = computed(() => isDemo.value ? (props.demoData.fixedWidgetsData || {}) : liveFixedWidgets.value)
const fixedWidgetsCompareToData = computed(() => isDemo.value ? (props.demoData.compareData || {}) : liveCompare.value)
const allZones = computed(() => isDemo.value ? (props.demoData.zones || []) : liveZones.value)
const guidanceRecommendations = computed(() => isDemo.value ? (props.demoData.recommendations || {}) : liveRecs.value)
// Digest is fetched by TodayView itself (below) so it's ready with everything else
// and never pops in after; CoachDigest just renders it (no self-fetch).
const digestForView = computed(() => isDemo.value ? props.demoData.digest : liveDigest.value)

// PROVE (value delivered) + Your goals. Demo: from the static bundle. Live: goals
// come from the home-summary; the measured value roll-up is a follow-up wire-up
// (guidance rollup), so it stays null (block self-hides) until then.
const liveGoals = ref([])
const liveValue = ref(null)
const goals = computed(() => isDemo.value ? (props.demoData.goals || []) : liveGoals.value)
const valueDelivered = computed(() => isDemo.value ? (props.demoData.valueDelivered || null) : liveValue.value)
// "since okt 2026" for a Dutch reader; English keeps the label it was built with.
const deliveredSince = computed(() => {
  const v = valueDelivered.value
  if (!v) return ''
  if (isNl.value && v.sinceRaw) {
    try {
      return new Date(v.sinceRaw).toLocaleDateString('nl', { month: 'short', year: 'numeric' })
    } catch (_) { return v.since }
  }
  return v.since
})
// Demo findings arrive already in AllenNoticed's shape; keep a local copy so
// dismiss works without a backend. Live findings come from the store (mapped below).
const localFindings = ref(props.demoData?.findings ? [...props.demoData.findings] : null)

const companyName = computed(() =>
  isDemo.value ? (props.demoData.companyName || t('today.yourStore')) : (companyStore.company?.CompanyName || t('today.yourStore')))
// Same derivations FixedWidgets uses — keep them in lock-step so the "/" Today
// reads a zone against the exact same ceiling as the dashboard.
const companySector = computed(() =>
  isDemo.value ? (props.demoData.sector || 'default') : (companyStore.company?.CompanySettings?.sector || 'default'))
const zoneOverrides = computed(() =>
  isDemo.value ? (props.demoData.zoneOverrides || {}) : buildZoneOverridesMap(detectionZoneStore))

// Zone counts / presence — same DetectionZoneTypeId filters the dashboard uses,
// derived from the zone list (live: store fetch; demo: demo_detection_zones bundle).
const countOfType = (id) => (allZones.value || []).filter((z) => z.DetectionZoneTypeId === id).length
const zoneTypeIds = computed(() => new Set((allZones.value || []).map((z) => z.DetectionZoneTypeId)))
// A zone with a real polygon (≥ 3 points). Live zones carry it as a JSON string on
// DetectionZonePointArray; the demo bundle as a parsed array on `Polygon`.
const hasPolygon = (z) => {
  let pts = z?.Polygon ?? z?.DetectionZonePointArray
  if (typeof pts === 'string') { try { pts = JSON.parse(pts) } catch { pts = null } }
  return Array.isArray(pts) && pts.length >= 3
}
// Feeds the € gauge's "Based on N Product zone(s)", so it counts only the zones that
// gauge actually uses. A tripwire-only product zone has no engagement/conversion
// funnel and storeEfficiency.js leaves it out of the € climb; counting it here made
// the pill claim zones the figure above it never included.
const productZoneCount = computed(() =>
  (allZones.value || []).filter((z) => z.DetectionZoneTypeId === 6 && hasPolygon(z)).length)
const waitingZoneCount = computed(() => countOfType(4))
const interactionLaneCount = computed(() => countOfType(5))
const cashierLaneCount = computed(() => countOfType(999))
const infoZoneCount = computed(() => countOfType(9))
const serviceZoneCount = computed(() => countOfType(10))
const pickUpZoneCount = computed(() => countOfType(11))
const hasProductZones = computed(() => zoneTypeIds.value.has(6))
const hasWaitingZones = computed(() => zoneTypeIds.value.has(4))
const hasInteractionZones = computed(() => zoneTypeIds.value.has(5))
// The whole surface (and the hero) keys off having a measurable product zone —
// matches FixedWidgets' :has-any-zones="isProductZonePresent()".
const hasAnyZones = computed(() => hasProductZones.value)

const companyStaffCost = computed(() => {
  if (isDemo.value) return props.demoData.staffCost ?? null
  const v = companyStore.company?.CompanySettings?.staffCostPerHour
  return (typeof v === 'number' && Number.isFinite(v) && v > 0) ? v : null
})

// WHEN the idle checkout hours fall — one sentence under DEPLOY, attributing the euro
// that gauge already shows. Null unless the waste is genuinely concentrated: spread
// evenly across the week it says nothing and must render nothing.
const idleWhenLive = ref(null)
const idleWhen = computed(() =>
  isDemo.value ? (props.demoData.idleWhen ?? null) : idleWhenLive.value)

const strategicByZone = computed(() => {
  try {
    return composeStrategicPositioning(fixedWidgetsData.value, companySector.value, zoneOverrides.value) || {}
  } catch (_) {
    return {}
  }
})

const priorities = computed(() => {
  try {
    return computePriorities(
      fixedWidgetsData.value,
      fixedWidgetsCompareToData.value,
      companySector.value,
      zoneOverrides.value,
      fixedWidgetsData.value?._cohort ?? null,
      selectedDuration,            // pinned 30 → within-reach € is already a true /mo
    ) || []
  } catch (_) {
    return []
  }
})

// The one canonical store opportunity €/mo: the sum of every zone's within-reach.
// Fed to BOTH the hero and the commercial-efficiency gauge so the headline always
// equals the sum of the per-zone cards below it (see sumWithinReach's note).
const storeWithinReach = computed(() => sumWithinReach(priorities.value))
// "Where to focus" unit: € when any zone has a € opportunity; an all-tripwire store
// (only arrivals counted) has its opportunities in visitors only.
const focusBasisRaw = computed(() =>
  priorities.value.some((p) => p.withinReachUnit !== 'visitors' && p.potentialImpact > 0)
    ? '€/mo' : 'visitors/mo')
const focusBasis = computed(() =>
  t(focusBasisRaw.value === '€/mo' ? 'today.focus.basisEur' : 'today.focus.basisVisitors'))

// Findings → the "what's changing" layer. Production findings are shaped
// { FindingId, Data:{severity,title,detail,when} }; map to AllenNoticed's lean
// shape and keep the high-severity one out (Home's banner owns that).
const SEV_RANK = { high: 0, warning: 1, info: 2, good: 3 }
const noticedFindings = computed(() => {
  // A finding about a zone that changed type today described it as its old type: hidden
  // until the nightly run brings back what still applies (6.3).
  if (isDemo.value) {
    return [...(localFindings.value || [])]
      .filter((f) => f.severity !== 'high' && !typeChangedZone(f.zoneKey, f.zone))
      .sort((a, b) => (SEV_RANK[a.severity] ?? 9) - (SEV_RANK[b.severity] ?? 9))
  }
  return [...(companyStore.findings || [])]
    .filter((f) => (f.Data && f.Data.severity) !== 'high'
      && !typeChangedZone(f.Data && f.Data.zoneKey, f.Data && f.Data.zone))
    .sort((a, b) => (SEV_RANK[a.Data && a.Data.severity] ?? 9) - (SEV_RANK[b.Data && b.Data.severity] ?? 9))
    .map((f) => ({
      id: f.FindingId,
      kind: f.Data && f.Data.kind,
      zone: f.Data && f.Data.zone,
      zones: f.Data && f.Data.zones,
      severity: (f.Data && f.Data.severity) || 'info',
      title: f.Data && f.Data.title,
      detail: f.Data && f.Data.detail,
      when: f.Data && f.Data.when,
      // Optional presentation hints (e.g. weather findings render teal with a glyph).
      accent: f.Data && f.Data.accent,
      icon: f.Data && f.Data.icon,
    }))
})

// v2 split of the flagged list. "Needs your attention" = what asks the manager to do
// something (warning/high), capped at 3. Trends, forecasts and weather (info) are
// context → "Trends & week ahead". Proof (a guidance verdict / 'good') is already the
// header badge + Value delivered, so it is not repeated as a flag.
const ATTENTION_CAP = 3
const kindOf = (f) => f.kind || null
// Proof = a tracked change's measured verdict (it lives in the header badge). By KIND
// only: other 'good' findings — the store was busier, an exceptional day — are news
// for Trends, not proof, and filtering on severity hid them from Home.
const isProof = (f) => kindOf(f) === 'guidance_verdict'
// New suggestions are not a problem to attend to — they become a "New" tag on their
// focus cards instead (newSuggestionZones), so they are left out of the attention list.
const isNewSuggestions = (f) => kindOf(f) === 'new_suggestions'
const attentionFindings = computed(() =>
  noticedFindings.value.filter((f) => !isProof(f) && !isNewSuggestions(f)
    && (f.severity === 'warning' || f.severity === 'high')))
// Live: from GET /coach/suggestions/new/ (the weekly message is a notification, not a
// finding, and stores no zone list). Demo: from the demo's notification item.
const liveNewZones = ref([])
const newSuggestionZones = computed(() => {
  if (!isDemo.value) return liveNewZones.value
  const f = noticedFindings.value.find(isNewSuggestions)
  if (!f) return []
  return f.zones || (f.zone ? [f.zone] : [])
})
// Goals already shown as a chip on a focus card are not listed again.
const focusZones = ref([])
const goalsWithoutCard = computed(() =>
  (goals.value || []).filter((g) => !focusZones.value.some((z) =>
    (g.zone && g.zone === z) || (!g.zone && String(g.title || '').includes(z)))))
const attentionShown = computed(() => attentionFindings.value.slice(0, ATTENTION_CAP))
// Weather outlooks and holiday outlooks (a school break / bridge day next week with a
// measured effect) are what a manager can PLAN for this week → "Plan ahead", at the
// top of the briefing's flagged block (not amber: not a problem).
const PLAN_AHEAD_KINDS = ['weather_outlook', 'calendar_outlook']
const isPlanAhead = (f) => PLAN_AHEAD_KINDS.includes(kindOf(f))
const planAheadFindings = computed(() => noticedFindings.value.filter(isPlanAhead))
// Everything else that is 'info' — footfall / zone outlooks and DWELL CHANGES
// (coach/findings.py::_dwell_change_findings, severity info: a change with no good or bad
// direction) — is context → "Trends & week ahead", never "Needs your attention".
const contextFindings = computed(() =>
  noticedFindings.value.filter((f) => !isProof(f) && !isPlanAhead(f)
    && f.severity !== 'warning' && f.severity !== 'high'))
const aheadIcon = (f) => {
  if (kindOf(f) === 'dwell_change') return 'bi-clock-history'
  if (kindOf(f) === 'store_day_promo') return 'bi-tag'          // a promo day, said quietly
  const t = String(f.title || '').toLowerCase()
  // Finding titles come in the reader's language: the Dutch words pick the same arrows.
  if (t.includes('fewer') || t.includes('down') || t.includes('minder') || t.includes('daal')) return 'bi-arrow-down-right'
  if (t.includes('more') || t.includes('up') || t.includes('meer') || t.includes('stijg')) return 'bi-arrow-up-right'
  return 'bi-graph-up'
}

// ---- Reach-only store (every product zone a counting line, no polygon) -------------
// Nothing can be valued in € there, so Home reads it through footfall + reach.
const reachOnly = computed(() => hasProductZones.value && productZoneCount.value === 0)
const storeVisitors = computed(() => {
  const fw = fixedWidgetsData.value || {}
  if (Number.isFinite(fw.total_visitors)) return Math.round(fw.total_visitors)
  const sv = Object.values(fw.product_zone_funnels || {}).map((f) => Number(f?.store_visitors) || 0)
  return sv.length ? Math.max(...sv) : 0
})
const visitorsPerDay = computed(() => Math.round(Number(fixedWidgetsData.value?.average_visitors) || 0))
// Per counting zone: share of shoppers who come now vs in its own best weeks (Rung 1,
// the same rung the reach cards use). A zone without a best-weeks reach sits at its own 100%.
const reachRows = computed(() => {
  const fw = fixedWidgetsData.value || {}
  const store = storeVisitors.value
  if (!store) return []
  const pct1 = (r) => `${(r * 100).toFixed(0)}%`
  return Object.entries(fw.product_zone_visitors_counts || {})
    .map(([zone, v]) => {
      const now = (Number(v) || 0) / store
      const ov = zoneOverrides.value?.[zone] || {}
      const best = Math.max(now, Number.isFinite(ov.rung1Reach) ? ov.rung1Reach : 0)
      const pct = best > 0 ? Math.round((now / best) * 100) : 100
      return {
        key: zone, name: zone, pct, atBest: pct >= 100, rungs: [{ key: 'best', pct: 100 }],
        text: pct >= 100
          ? t('today.health.attract.rowAtBest', { now: pct1(now) })
          : t('today.health.attract.rowBelowBest', { now: pct1(now), best: pct1(best) }),
        note: dwellNote(zone, Number(v) || 0),
        _now: now * store, _best: best * store,
      }
    })
    .sort((a, b) => a.pct - b.pct)
})
const reachStorePct = computed(() => {
  const now = reachRows.value.reduce((s, r) => s + r._now, 0)
  const best = reachRows.value.reduce((s, r) => s + r._best, 0)
  return best > 0 ? Math.round((now / best) * 100) : 100
})
// The zone's estimated dwell — or, when it is timed from another line, its transit time —
// as CONTEXT under its reach line. Never on the bar and never judged: neither has a good
// direction or a best-weeks benchmark. Shown only at medium/high confidence (30 days).
const fmtDurSec = (sec) => {
  const t = Math.round(Number(sec))
  if (!Number.isFinite(t) || t <= 0) return ''
  if (t < 60) return `${t} sec`
  const m = Math.floor(t / 60), r = t % 60
  return r ? `${m} min ${r} sec` : `${m} min`
}
function dwellNote(zone, visitors) {
  const fw = fixedWidgetsData.value || {}
  if (!(fw.product_zones_with_estimated_dwell || []).includes(zone) || !visitors) return ''
  const conf = (fw.product_zones_dwell_confidence || {})[zone]
  if (conf !== 'medium' && conf !== 'high') return ''
  const avg = Number((fw.total_duration_per_product_zone || {})[zone] || 0) / visitors
  const dur = fmtDurSec(avg)
  if (!dur) return ''
  const src = (fw.product_zones_transit_source || {})[zone]
  return src ? t('today.health.attract.transit', { dur, src }) : t('today.health.attract.stays', { dur })
}
// Store visit length (the store-level estimator) for the footfall headline; withheld at
// low confidence. Whole minutes from 10 min up — a headline, not a stopwatch.
const storeVisitLabel = computed(() => {
  const fw = fixedWidgetsData.value || {}
  const sec = Number(fw.store_dwell_estimate_seconds)
  if (!Number.isFinite(sec) || sec <= 0) return ''
  if (fw.store_dwell_confidence !== 'medium' && fw.store_dwell_confidence !== 'high') return ''
  return sec >= 600 ? `${Math.round(sec / 60)} min` : fmtDurSec(sec)
})

// Same total as the reach cards above (Σ visitors within reach), so the row and the
// cards can never disagree.
const reachWithin = computed(() => Math.round(priorities.value
  .filter((p) => p.withinReachUnit === 'visitors')
  .reduce((s, p) => s + (p.withinReachMagnitude || 0), 0)))

// "Based on N counting-line Product zone(s)" — component-built HTML for HealthRow's meta.
const reachMeta = computed(() => {
  const n = reachRows.value.length
  const product = `<strong>${t('today.health.attract.product', n)}</strong>`
  return t('today.health.attract.basedOn', { n, product }, n)
})

// What more zones would add — named, not shown as "Not configured" rows.
const unlockItems = computed(() => [
  productZoneCount.value === 0 && hasProductZones.value ? t('today.health.unlockEur') : null,
  !hasWaitingZones.value ? t('today.health.unlockWait') : null,
  !hasInteractionZones.value ? t('today.health.unlockStaffing') : null,
].filter(Boolean))
const unlockText = computed(() => {
  const xs = unlockItems.value
  return xs.length > 1 ? `${xs.slice(0, -1).join(', ')} ${t('today.health.and')} ${xs[xs.length - 1]}` : (xs[0] || '')
})
const UNLOCK_KEY = 'stora_home_unlock_hidden_until'
const unlockHiddenUntil = ref((() => {
  try { return Number(localStorage.getItem(UNLOCK_KEY) || 0) } catch (_) { return 0 }
})())
const userStore = useUserStore()
const isAdmin = computed(() => isDemo.value || !!userStore.isAdminInCurrentCompany)
const showUnlock = computed(() => isAdmin.value && Date.now() > unlockHiddenUntil.value)
function dismissUnlock() {
  unlockHiddenUntil.value = Date.now() + 30 * 86400000
  try { localStorage.setItem(UNLOCK_KEY, String(unlockHiddenUntil.value)) } catch (_) { /* session only */ }
}

// Shared % scale for the ladder rows (ATTRACT + SERVE): each gauge reports the highest
// % it must show; the block uses the larger, with the same 6% headroom + round-to-10
// the gauges use on their own.
const axisTops = reactive({ attract: null, serve: null })
// Capped so "your best" (100%) always sits at least HALFWAY along the bar: a far-off
// rung (e.g. best-in-class at 244%) would otherwise squeeze every row's own climb into
// the left third. A rung past the cap is drawn at the bar's end with its true value.
// The cap follows from the reference mark (100% at mid-bar), not a tuned number.
const AXIS_CAP = 200
const sharedAxisMax = computed(() => {
  const tops = [axisTops.attract, axisTops.serve].filter((v) => Number.isFinite(v))
  if (!tops.length) return null
  return Math.min(AXIS_CAP, Math.max(100, Math.ceil((Math.max(...tops) * 1.06) / 10) * 10))
})

// Compare-window sentinel (mirrors Home.vue): 30d → compare_to_30_days (7000).
const COMPARE_SENTINEL = { 1: 5000, 7: 6000, 30: 7000, 90: 8000 }

async function loadFixedWidgets() {
  try {
    const full = await getFixedDashboardCountersValues(selectedDuration, openingHoursMode)
    const zt = await getZoneTypeAnalytics(selectedDuration, openingHoursMode, [5, 999])
    const t5 = zt?.zone_types?.['5'] || null
    const t999 = zt?.zone_types?.['999'] || null
    liveFixedWidgets.value = {
      ...full,
      average_same_weekday: Math.round(full.average_same_weekday),
      average_visitors: Math.round(full.average_visitors),
      average_on_busiest_hour: Math.round(full.average_on_busiest_hour),
      date: full.last_updated_date,
      checkout_interaction_usage_pct: t5?.usage_percentage ?? null,
      checkout_interaction_total_visitors: t5?.total_visitors ?? null,
      checkout_interaction_total_time_seconds: t5?.total_time_in_zone_seconds ?? null,
      checkout_interaction_total_opening_hours: t5?.total_opening_hours ?? null,
      checkout_cashier_usage_pct: t999?.usage_percentage ?? null,
      checkout_cashier_total_visitors: t999?.total_visitors ?? null,
      checkout_cashier_total_time_seconds: t999?.total_time_in_zone_seconds ?? null,
      checkout_cashier_total_opening_hours: t999?.total_opening_hours ?? null,
    }
  } catch (e) {
    liveFixedWidgets.value = {}
  }

  const cmp = COMPARE_SENTINEL[selectedDuration] || 0
  if (cmp) {
    try {
      const fullC = await getFixedDashboardCountersValues(cmp, openingHoursMode)
      const ztC = await getZoneTypeAnalytics(cmp, openingHoursMode, [5, 999])
      const t5c = ztC?.zone_types?.['5'] || null
      const t999c = ztC?.zone_types?.['999'] || null
      liveCompare.value = {
        ...fullC,
        checkout_interaction_usage_pct: t5c?.usage_percentage ?? null,
        checkout_cashier_usage_pct: t999c?.usage_percentage ?? null,
      }
    } catch (_) { /* compare is optional */ }
  }
}

onMounted(async () => {
  if (!isDemo.value) changesTodayStore.ensureLoaded(null)
  if (isDemo.value) { emit('resolved', hasAnyZones.value); return } // static bundle — no fetches
  try {
    await detectionZoneStore.fetchDetectionZones()
    liveZones.value = detectionZoneStore.detectionzones || []
  } catch (_) { liveZones.value = [] }
  await loadFixedWidgets()
  try {
    liveRecs.value = (await companyStore.fetchGuidanceRecommendations().catch(() => ({}))) || {}
  } catch (_) { liveRecs.value = {} }
  // Fire-and-forget: the gauge renders without it and gains the line when it lands.
  // A store with no concentrated idle hours resolves to null and shows nothing, which
  // is the answer for most of them.
  getCheckoutIdlePattern().then((v) => { idleWhenLive.value = v })
  // NEW tags on the focus cards — decorative, so fire-and-forget like the idle line.
  companyStore.fetchNewSuggestionZones().then((z) => { liveNewZones.value = z })
  // Findings (the "flagged" list) — fetch here so they're part of the loading gate
  // and don't pop in after the rest of the surface. Populates the shared store,
  // which HomeOverview's critical-alert + fallback also read.
  try { await companyStore.fetchFindings() } catch (_) { /* leave whatever's there */ }
  // Digest — fetch the synthesis variant ourselves so it's ready with everything
  // else (CoachDigest below just renders it via :static-digest, no self-fetch).
  try {
    liveDigest.value = await companyStore.fetchDigest('past_30_days', { style: 'synthesis' })
  } catch (_) { liveDigest.value = null }
  // Goals for the PROVE row — reuse the home-summary the landing already serves.
  try {
    const summary = await companyStore.fetchHomeSummary()
    liveGoals.value = (summary?.goals || []).map((g) => {
      const m = String((g.GoalData && g.GoalData.metric) || '').toLowerCase()
      const lever = m.includes('convers') ? 'conversion' : m.includes('engage') ? 'engagement' : m.includes('reach') ? 'reach' : ''
      // zone (current name, server-side) lets a goal find its focus card even when its
      // title doesn't spell the zone out.
      return { title: (g.GoalData && g.GoalData.title) || t('today.untitledGoal'), lever,
               zone: (g.GoalData && g.GoalData.zone) || undefined,
               zoneKey: (g.GoalData && g.GoalData.zoneKey) ?? undefined }
    })
  } catch (_) { liveGoals.value = [] }
  // Value delivered (PROVE) — measured roll-up. Backend { run_rate, cumulative,
  // since } → the panel's shape; 204/absent → stays null (block self-hides).
  try {
    const r = await companyStore.fetchGuidanceRollup()
    if (r) {
      const sinceLabel = (() => {
        if (!r.since) return ''
        try { return new Date(r.since).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) } catch (_) { return '' }
      })()
      liveValue.value = { runRate: Math.round(r.run_rate), cumulative: Math.round(r.cumulative || 0), since: sinceLabel, sinceRaw: r.since || null }
    }
  } catch (_) { liveValue.value = null }
  // Everything's in — reveal the whole surface at once and tell the host.
  loading.value = false
  emit('resolved', hasAnyZones.value)
})
</script>

<style scoped>
.today { display: flex; flex-direction: column; gap: 30px; max-width: 1060px; padding-top: 4px; }
.today-sec { min-width: 0; }

/* Section header (icon + label) — matches the demo Today rhythm. */
.today-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
.today-head-title {
  margin: 0; font-size: 13.5px; font-weight: 700; letter-spacing: 0.2px; color: #2b3a46;
  display: flex; align-items: center; gap: 8px;
}
.today-head-title .bi { color: var(--stora-accent, #4F7EB3); font-size: 15px; }
/* Basis caption — makes the fixed 30-day / €-per-month window explicit rather
   than a silent assumption (Today has no period picker by design). */
.today-basis { font-weight: 500; font-size: 11px; letter-spacing: 0; color: #9aa7b3; }
.today-head-tip { color: #9aa7b3; font-size: 14px; display: inline-flex; }
.today-head-tip:hover { color: var(--stora-accent, #4F7EB3); }
.today-link {
  border: none; background: none; cursor: pointer; text-decoration: none;
  font-size: 12px; font-weight: 600; color: var(--stora-accent, #4F7EB3);
  display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  padding: 2px 2px 2px 6px; border-radius: 6px; transition: background .15s ease;
}
.today-link:hover { background: #eef4fb; }

/* PROVE row — value delivered + goals */
.today-duo { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.today-panel {
  background: #fff; border: 1px solid #e8eef7; border-radius: 14px;
  padding: 16px 18px 18px; box-shadow: 0 3px 10px rgba(0,0,0,0.03);
}
.today-panel .today-head { margin-bottom: 12px; }
.today-panel--value { border-left: 3px solid #62a583; }
.value-hero { display: flex; align-items: baseline; gap: 8px; }
.value-eur { font-size: 30px; font-weight: 800; color: #2c6f58; line-height: 1; letter-spacing: -0.5px; }
.value-per { font-size: 12px; font-weight: 600; color: #6b7a87; }
.value-context { margin: 6px 0 0; font-size: 11.5px; line-height: 1.35; color: #6b7a87; max-width: 34ch; }
.value-sub { margin-top: 10px; font-size: 12px; color: #6b7a87; display: flex; align-items: center; gap: 7px; }
.value-sub .bi { color: #7fb096; font-size: 13px; }
.today-goals { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 11px; }
.today-goal { display: flex; align-items: flex-start; gap: 10px; }
.goal-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 5px; flex: 0 0 auto; background: #8fa2b7; }
.goal-dot.lever-conversion { background: #4F7EB3; }
.goal-dot.lever-engagement { background: #97a83f; }
.goal-dot.lever-reach { background: #C08A2E; }
.today-goal-title { font-size: 13px; color: #2b3a46; line-height: 1.4; }
@media (max-width: 860px) { .today-duo { grid-template-columns: 1fr; } }

/* ---- The lead: one Allen briefing card (mirrors the demo Today briefing) ---- */
.allen-briefing {
  background: #fff;
  border: 1px solid #e8eef7;
  border-radius: 16px;
  padding: 20px 24px 22px;
  box-shadow: 0 8px 22px -14px rgba(13,38,76,0.16);
}
.allen-briefing-head {
  display: flex; align-items: center; gap: 12px;
  padding-bottom: 14px; margin-bottom: 16px;
  border-bottom: 1px solid #eef2f7;
}
.allen-briefing-avatar {
  width: 36px; height: 36px; border-radius: 50%; flex: 0 0 auto;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #4F7EB3, #62B5D9); color: #fff; font-size: 1.05rem;
  box-shadow: 0 4px 10px rgba(79,126,179,0.30);
}
.allen-briefing-id { display: flex; flex-direction: column; gap: 1px; }
.allen-briefing-title { font-weight: 700; color: #1d2b3a; font-size: 1.02rem; line-height: 1.2; }
.allen-briefing-meta { font-size: 0.78rem; color: #8a99a4; }
/* The period basis, as a small chip so the fixed 30-day / €-per-month window
   reads as an explicit status, not a silent assumption (Today has no picker). */
.briefing-basis {
  display: inline-block;
  margin-left: 2px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #eef3f8;
  color: #5a6b78;
  font-weight: 600;
  font-size: 0.72rem;
}
.allen-briefing-read { display: block; margin-top: 16px; color: #425461; font-size: 0.92rem; line-height: 1.6; }
.allen-briefing-flagged { display: block; margin-top: 18px; padding-top: 16px; border-top: 1px solid #eef2f7; }
/* Reach-only headline (footfall) — same weight as the TodayHero headline. */
.footfall-head { margin: 2px 0 4px; }
.footfall-title { margin: 0; font-size: 1.45rem; font-weight: 700; color: #1d2b3a; line-height: 1.3; }
.footfall-n { color: #2c6f58; }
.footfall-sub { font-weight: 600; color: #51637a; }
.footfall-est { font-style: normal; font-weight: 500; font-size: 0.8em; color: #8a99a4; }
/* In a reach-only store the reach block IS "Where to focus" — no second heading. */
.today :deep(.pz-reach--alone .reach-head) { display: none; }
/* What more zones would add */
.unlock-strip {
  margin-top: 10px; display: flex; align-items: center; gap: 12px;
  padding: 10px 14px; border-radius: 10px; font-size: 13px; color: #51637a;
  background: #f7f9fc; border: 1px dashed #cdd8e6;
}
.unlock-icon { font-size: 16px; color: #4F7EB3; }
.unlock-body { flex: 1 1 auto; line-height: 1.45; }
.unlock-body strong { color: #2b3a46; }
.unlock-close { border: 0; background: none; color: #9aa7b3; cursor: pointer; font-size: 18px; line-height: 1; padding: 0 2px; }
.unlock-close:hover { color: #51637a; }
/* Second flagged list right under "Plan ahead": no second rule, tighter gap. */
.allen-briefing-flagged.flagged-follow { margin-top: 14px; padding-top: 0; border-top: 0; }
.flagged-seeall {
  margin-top: 10px; padding: 0; border: 0; background: transparent; cursor: pointer;
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 0.8rem; font-weight: 600; color: #4F7EB3;
}
.flagged-seeall:hover { color: #3a6699; text-decoration: underline; }
.flagged-seeall .bi { font-size: 0.75rem; }

/* ===== v2 additions ===== */
/* Proof badge — the measured run-rate, top-right of the briefing head. */
.proof-badge {
  margin-left: auto; text-decoration: none;
  display: inline-flex; align-items: baseline; gap: 6px;
  padding: 6px 12px; border-radius: 999px;
  background: #eef7f1; border: 1px solid #cfe6d8; color: #2c6f58;
  transition: background .15s ease;
}
.proof-badge:hover { background: #e2f1e8; }
.proof-badge .bi { font-size: 0.9rem; align-self: center; }
.proof-eur { font-weight: 800; font-size: 1rem; letter-spacing: -0.2px; }
.proof-lbl { font-size: 0.74rem; font-weight: 600; color: #4f8a72; }

/* Store health — one card, three scorecard rows divided by hairlines. */
.health-rows {
  background: #fff; border: 1px solid #e8eef7; border-radius: 14px;
  box-shadow: 0 3px 10px rgba(0,0,0,0.03); overflow: hidden;
}
.health-row-tap { cursor: pointer; transition: background .15s ease; }
.health-row-tap + .health-row-tap { border-top: 1px solid #eef2f7; }
.health-row-tap:hover { background: #fafcfe; }

/* Trends & week ahead — compact two-column grid, quieter than the attention list. */
.ahead-grid {
  list-style: none; padding: 0; margin: 0;
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px;
}
.ahead-item {
  display: flex; gap: 10px; align-items: flex-start;
  background: #f7f9fc; border: 1px solid #edf1f6; border-radius: 10px; padding: 10px 12px;
}
.ahead-item.accent-weather { background: #f0f8f5; border-color: #d9ede5; }
.ahead-glyph { font-size: 15px; color: #6b8fb8; margin-top: 1px; }
.ahead-item.accent-weather .ahead-glyph { color: #2f8f7a; }
.ahead-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.ahead-title { font-weight: 600; font-size: 0.9rem; color: #1d2b3a; line-height: 1.3; }
.ahead-detail {
  font-size: 0.84rem; color: #51637a; line-height: 1.4;
}
.ahead-when { font-size: 12px; color: #8a99a4; margin-top: 2px; }
@media (max-width: 860px) { .ahead-grid { grid-template-columns: 1fr; } }

/* Close — banked value strip + goals without a card */
.today-close { display: flex; flex-direction: column; gap: 10px; }
.value-strip {
  display: flex; align-items: center; gap: 14px; text-decoration: none;
  background: #fff; border: 1px solid #e8eef7; border-left: 3px solid #62a583; border-radius: 12px;
  padding: 12px 16px; box-shadow: 0 3px 10px rgba(0,0,0,0.03); color: #51637a; font-size: 13px;
}
a.value-strip:hover { background: #fafdfb; }
.value-strip-icon { font-size: 20px; color: #62a583; }
.value-strip-body { display: flex; flex-direction: column; gap: 1px; flex: 1 1 auto; line-height: 1.4; }
.value-strip-body strong { color: #2c6f58; font-size: 15px; }
.value-strip-link { font-size: 13px; font-weight: 600; color: var(--stora-accent, #4F7EB3); white-space: nowrap; }
.value-strip--nudge { border-left-color: #C08A2E; }
.value-strip--nudge .value-strip-icon { color: #C08A2E; }
.value-strip--nudge .value-strip-body strong { color: #2b3a46; }
/* 6.3: a goal whose zone changed type today — kept, flagged (calm blue, ChangeNotice tone) */
.goal-type-note { color: #2C5F86; font-size: 12px; margin-left: 6px; }
.goals-rest { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 16px; font-size: 13px; color: #2b3a46; padding: 2px 4px; }
.goals-rest-lbl { font-size: 12px; font-weight: 700; color: #6b7a87; display: inline-flex; gap: 6px; align-items: center; }
.goals-rest-item { display: inline-flex; align-items: center; gap: 7px; }
.goals-rest .goal-dot { margin-top: 0; }

/* Legibility on a shop-floor tablet: nothing below 12px on this page. */
.today :deep(.finding-when),
.today :deep(.lean-reach-lbl),
.today :deep(.lean-basis),
.today :deep(.lean-goal),
.today :deep(.hero-state-est) { font-size: 12px !important; }
.today :deep(.finding-title) { font-size: 0.9rem; }
.today :deep(.finding-detail) { font-size: 0.84rem; }
.today-basis { font-size: 12px; }
.allen-briefing-meta { font-size: 0.8rem; }

.today-duo--single { grid-template-columns: 1fr; max-width: 520px; }
.value-nudge {
  margin: 14px 0 0; padding-top: 12px; border-top: 1px solid #eef2f7;
  font-size: 12px; color: #6b7a87; display: flex; gap: 7px; align-items: flex-start;
}
.value-nudge .bi { color: #C08A2E; }
</style>
